
import { replace, split, toConsole, printf, toText, isNullOrWhiteSpace } from "../fable_modules/fable-library-js.5.3.0/String.js";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { ofNullable } from "../fable_modules/fable-library-js.5.3.0/Option.js";
import { toString } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { singleton } from "../fable_modules/fable-library-js.5.3.0/AsyncBuilder.js";
import { awaitPromise } from "../fable_modules/fable-library-js.5.3.0/Async.js";
import { item } from "../fable_modules/fable-library-js.5.3.0/Array.js";

export const Config_redirectUri = ((typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:8080')) + "/auth/callback";

export const Config_postLogoutRedirectUri = ((typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:8080')) + "/";

export const Config_responseType = "code";

/**
 * OIDC scope for the SPA. A non-blank org ID pins the auth request to that Zitadel org
 * (its login policy applies and only its users can sign in).
 */
export function scopeFor(orgId) {
    if (isNullOrWhiteSpace(orgId)) {
        return "openid profile email";
    }
    else {
        const arg_1 = orgId.trim();
        return toText(printf("%s urn:zitadel:iam:org:id:%s"))("openid profile email")(arg_1);
    }
}

function createUserManager() {
    const userStore = new WebStorageStateStore({
        store: window.localStorage,
    });
    const settings = {
        authority: window.ZITADEL_AUTHORITY || 'http://localhost:8081',
        client_id: window.ZITADEL_CLIENT_ID,
        redirect_uri: Config_redirectUri,
        post_logout_redirect_uri: Config_postLogoutRedirectUri,
        response_type: Config_responseType,
        scope: scopeFor(window.ZITADEL_ORG_ID || ''),
        automaticSilentRenew: true,
        loadUserInfo: true,
        userStore: userStore,
    };
    return new UserManager(settings);
}

let _userManager = undefined;

function getUserManager() {
    if (_userManager == null) {
        const um_1 = createUserManager();
        _userManager = um_1;
        return um_1;
    }
    else {
        return _userManager;
    }
}

const TokenStorage_tokenKey = "you-thon-auth-token";

export function TokenStorage_save(token) {
    window.localStorage.setItem(TokenStorage_tokenKey, token);
    document.cookie = toText(printf("auth_token=%s; path=/; max-age=86400; SameSite=Lax"))(token);
}

export function TokenStorage_load() {
    return ofNullable(window.localStorage.getItem(TokenStorage_tokenKey));
}

export function TokenStorage_remove() {
    window.localStorage.removeItem(TokenStorage_tokenKey);
    document.cookie = "auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
}

/**
 * Start the OIDC login redirect, carrying `returnPath` through the round-trip
 * as the OIDC `state` so the callback can return to the page login started from.
 */
export function login(returnPath) {
    const args = {
        state: returnPath,
    };
    return getUserManager().signinRedirect(args);
}

/**
 * The return path `login` stored in the OIDC `state`, read from the user
 * returned by `handleCallback`.
 */
export function returnPathOf(user) {
    if ((user == null) ? true : (user.state == null)) {
        return undefined;
    }
    else {
        return toString(user.state);
    }
}

export function register() {
    const args = {
        extraQueryParams: {
            prompt: "create",
        },
    };
    return getUserManager().signinRedirect(args);
}

export function handleCallback() {
    toConsole(printf("Auth: Handling OIDC callback..."));
    const promise = getUserManager().signinRedirectCallback();
    promise.then((result) => {
        toConsole(printf("Auth: Callback handled, result: %A"))(result);
        getUserManager().getUser().then((userOpt) => {
            if (!(userOpt == null)) {
                TokenStorage_save(toString(userOpt.access_token));
                toConsole(printf("Auth: Token saved to cookie for SSR"));
            }
            return userOpt;
        });
        return result;
    });
    return promise;
}

export function removeUser() {
    return getUserManager().removeUser();
}

export function logout() {
    TokenStorage_remove();
    const um = getUserManager();
    return um.removeUser().catch(() => undefined).then(() => um.signoutRedirect());
}

export function getUser() {
    toConsole(printf("Auth: Getting user from OIDC..."));
    const promise = getUserManager().getUser();
    promise.then((userOpt) => {
        const arg = !(userOpt == null);
        toConsole(printf("Auth: User from OIDC: %A"))(arg);
        return userOpt;
    });
    return promise;
}

export function isAuthenticated() {
    return singleton.Delay(() => singleton.Bind(awaitPromise(getUser()), (_arg) => singleton.Return(!(_arg == null))));
}

/**
 * Decode a JWT payload and extract the email claim, if present.
 * JWTs are three base64url-encoded segments separated by dots.
 */
export function decodeJwtEmail(token) {
    try {
        const parts = split(token, ["."], undefined, 0);
        if (parts.length < 2) {
            return undefined;
        }
        else {
            let payload;
            const s = replace(replace(item(1, parts), "-", "+"), "_", "/");
            const matchValue = (s.length % 4) | 0;
            payload = ((matchValue === 2) ? (s + "==") : ((matchValue === 3) ? (s + "=") : s));
            const json = JSON.parse(atob(payload));
            const email = json.email;
            return (email == null) ? undefined : toString(email);
        }
    }
    catch (matchValue_1) {
        return undefined;
    }
}

/**
 * Check whether a JWT's `exp` claim is in the past.
 */
export function isJwtExpired(token) {
    try {
        const parts = split(token, ["."], undefined, 0);
        if (parts.length < 2) {
            return true;
        }
        else {
            let payload;
            const s = replace(replace(item(1, parts), "-", "+"), "_", "/");
            const matchValue = (s.length % 4) | 0;
            payload = ((matchValue === 2) ? (s + "==") : ((matchValue === 3) ? (s + "=") : s));
            const json = JSON.parse(atob(payload));
            const exp = json.exp;
            return (exp == null) ? true : (exp <= (Date.now() / 1000));
        }
    }
    catch (matchValue_1) {
        return true;
    }
}

//# sourceMappingURL=Auth.js.map
